import { DiscoveredItem } from 'models/DiscoverPortalActivityAction';
import {
  createdItems,
  DiscoveryDisposition,
  DispositionItem,
  toDiscoveryDisposition,
} from 'models/DisposePortalActivityAction';
import Workflow, { ActionStatus, WorkflowStatus } from 'models/Workflow';
import { PortalScanResult } from 'services/api/portalScans';

import { ACTION_KINDS } from './constants';
import { TaskSort } from './taskCards';

/**
 * The Portal Scans list, derived from `portal_discovery` index rows.
 *
 * Everything the card needs is already on the row: the engine publishes a completed action's
 * outputs as workflow-level values, so `discovered_items` and `discovery_disposition` ride along
 * on the list payload. No per-run show fetch, and therefore no N+1 of them behind this list.
 */

// The two outputs a sweep leaves behind, as VALUE names (snake_case, verbatim — they are the
// engine's own names, not API field keys).
const DISCOVERED_ITEMS = 'discovered_items';
const DISCOVERY_DISPOSITION = 'discovery_disposition';

// A run canceled because a newer sweep replaced it. Not a failure — nothing went wrong — so it
// must not take the red treatment.
const SUPERSEDED = 'superseded';

/**
 * Four states, not the design's two.
 *
 * `nothingFound` is a RESULT, not an empty state: a sweep that ran and found no new referrals
 * did its job. It gets neither the red failure treatment nor a link to an empty table.
 *
 * `running` exists because a live scan is a row in this list from the moment it starts, and
 * every other state would misreport it — a scan in flight has created nothing yet, which is
 * indistinguishable from "found nothing" if you only look at the count.
 */
export type ScanState = 'running' | 'success' | 'nothingFound' | 'failed';

// One row of the results table: a created entry joined to the demographics of the sweep entry
// it came from. `workflowId` is never null — the api writes one exactly for `created` items,
// which is the only disposition that reaches this table.
export interface ScanResultRow {
  key: string;
  workflowId: string;
  patientName: string | null;
  patientDob: string | null;
  facilityName: string | null;
}

export interface ScanCard {
  id: string;
  workflowId: string;
  sourceSystem: string;
  portalName: string;
  state: ScanState;
  title: string;
  // The line under the title. For a linked card this is the count; otherwise it says what
  // happened in words.
  detail: string;
  // Referrals CREATED — not entries retrieved. Entries the watermark passed over are counted
  // nowhere and listed nowhere, by design.
  resultCount: number;
  // When the scan finished. Null while it is still running, and never silently substituted with
  // createdAt — "scanned on" and "asked for on" are different claims.
  completedAt: Date | null;
  createdAt: Date | null;
  rows: ScanResultRow[];
  // Whether the count opens the results slideout. False on every non-success state, and false on
  // a success whose per-item verdicts predate the api recording them — there is a real count but
  // nothing to list, and a link to an empty table would misrepresent it.
  canOpenResults: boolean;
}

export interface ScanCardContext {
  // usePortalName's resolver. The server owns portal names; this module holds no catalog.
  portalName: (sourceSystem?: string | null) => string;
}

function valueOf(workflow: Workflow, name: string): unknown {
  return workflow.valueFor(name)?.value as unknown;
}

function discoveredItems(workflow: Workflow): DiscoveredItem[] {
  const value = valueOf(workflow, DISCOVERED_ITEMS);
  return Array.isArray(value) ? (value as DiscoveredItem[]) : [];
}

/**
 * The results table's rows: created entries, joined to the sweep's own captured demographics on
 * `reference_or_url`.
 *
 * The join CONSUMES its matches rather than reading from a plain lookup, because a pointer is
 * not unique within one sweep — the spawner absorbs a duplicate pointer as `skipped`, but the
 * discovered list can still carry the same URL twice. A shared map would hand both created rows
 * the same demographics; a consuming queue gives each row the next unclaimed entry, which is the
 * same first-match order the api disposed them in.
 */
export function joinScanRows(created: DispositionItem[], discovered: DiscoveredItem[]): ScanResultRow[] {
  const queues = new Map<string, DiscoveredItem[]>();
  discovered.forEach((item) => {
    const key = item.referenceOrUrl;
    if (!key) return;
    const queue = queues.get(key);
    if (queue) queue.push(item);
    else queues.set(key, [item]);
  });

  return created.map((item, index) => {
    const pointer = item.referenceOrUrl ?? '';
    const match = queues.get(pointer)?.shift() ?? null;

    return {
      key: item.workflowId ?? `${pointer}:${index}`,
      workflowId: item.workflowId ?? '',
      // The disposition's own name wins: it is what the run was actually created with. The
      // discovered entry is the fallback for a verdict recorded without one.
      patientName: item.patientName || match?.patientName || null,
      patientDob: match?.patientDob || null,
      facilityName: match?.facilityName || null,
    };
  });
}

const SWEEP_KINDS: string[] = [ACTION_KINDS.discover, ACTION_KINDS.disposeSweep];

function scanState(workflow: Workflow, resultCount: number): ScanState {
  // Behavior keys off `status`, never `effectiveStatus` — the latter is presentation-only.
  // Either half of the sweep erroring fails the card: an errored dispose keeps the run
  // non-terminal just like an errored discover, and without this it would read
  // "scan in progress" forever.
  const errored = workflow.actions.some(
    (action) => SWEEP_KINDS.includes(action.kind) && action.status === ActionStatus.ERRORED
  );
  const abandoned = workflow.status === WorkflowStatus.CANCELED && workflow.outcome !== SUPERSEDED;
  if (errored || abandoned) return 'failed';

  // A sweep parked on a person (`action_required`, the normal M2 resting state) is in flight,
  // not failed and not finished. Only a terminal run reports a result.
  if (!workflow.isTerminal) return 'running';

  return resultCount > 0 ? 'success' : 'nothingFound';
}

export function scanCard(workflow: Workflow, { portalName }: ScanCardContext): ScanCard {
  const sourceSystem = workflow.valueFor('source_system')?.value ?? '';
  const portal = portalName(sourceSystem) || 'The portal';

  const summary = valueOf(workflow, DISCOVERY_DISPOSITION);
  const disposition = toDiscoveryDisposition(summary);
  const created = createdItems(disposition);
  const found = discoveredItems(workflow);

  // The count is `created` items, and ONLY that — never `discovered_items.length`, which is the
  // entries retrieved. The pre-verdict summary shape (runs disposed before per-item verdicts
  // were recorded) has no `items`, so its integer is the only number available; the card then
  // shows it without a link, rather than claiming zero.
  const legacyCreated = (summary as Partial<DiscoveryDisposition> | undefined)?.created;
  const resultCount = disposition ? created.length : typeof legacyCreated === 'number' ? legacyCreated : 0;

  const state = scanState(workflow, resultCount);
  const rows = state === 'success' ? joinScanRows(created, found) : [];

  return {
    id: workflow.id,
    workflowId: workflow.id,
    sourceSystem,
    portalName: portal,
    state,
    title: scanTitle(state, portal),
    detail: scanDetail(state, resultCount, found.length),
    resultCount,
    completedAt: workflow.completedAt,
    createdAt: workflow.createdAt,
    rows,
    canOpenResults: state === 'success' && rows.length > 0,
  };
}

function scanTitle(state: ScanState, portal: string): string {
  switch (state) {
    case 'failed':
      return `${portal} failed to scan`;
    case 'running':
      return `${portal} scan in progress`;
    case 'nothingFound':
      // NOT in the Figma — flagged for Design. It has to say the scan succeeded out loud, so it
      // borrows neither the failure's red nor a success card's link to an empty table.
      return `${portal} was scanned — no new referrals found`;
    default:
      return `${portal} was successfully scanned!`;
  }
}

function scanDetail(state: ScanState, resultCount: number, foundCount: number): string {
  switch (state) {
    case 'failed':
      return 'No results';
    case 'running':
      return 'Results appear here when the scan finishes.';
    case 'nothingFound':
      // The data distinguishes two genuinely different sentences, and the count semantics fold
      // them both to zero: a portal with nothing in it, versus a portal whose activity this
      // client has already handled.
      return foundCount > 0 ? 'Nothing new since the last scan.' : 'No new referral activity in the portal.';
    default:
      return resultCount === 1 ? '1 result' : `${resultCount} results`;
  }
}

/**
 * What actually happened, per portal — one sentence per outcome group.
 *
 * A multi-select against a per-portal singleton makes the middle case routine: check three
 * portals where one is already scanning, and two runs start. Reporting all three as "started"
 * leaves a list that gained two cards looking like a bug, so the three outcomes are reported
 * apart and a portal that was already scanning is never presented as a silent no-op.
 */
export function reportScanResults(
  results: PortalScanResult[],
  portalName: (sourceSystem?: string | null) => string
): string[] {
  const list = (group: PortalScanResult[]) =>
    group.map((result) => portalName(result.sourceSystem) || result.sourceSystem).join(', ');

  const started = results.filter((result) => result.created);
  const alreadyRunning = results.filter((result) => !result.created && result.workflowId);
  const refused = results.filter((result) => !result.created && !result.workflowId);

  const messages: string[] = [];
  if (started.length > 0) messages.push(`Scanning ${list(started)}.`);
  if (alreadyRunning.length > 0) messages.push(`A scan was already in progress for ${list(alreadyRunning)}.`);
  if (refused.length > 0) messages.push(`${list(refused)} can’t be scanned right now.`);
  // Never silent: a request that came back with nothing at all still owes the person an answer.
  if (messages.length === 0) messages.push('No scans were started.');
  return messages;
}

/**
 * Newest first, by when the scan was STARTED. Deliberately not by completion: a running scan has
 * no completion time, and sorting on a mixed key would drop live scans to the bottom of the very
 * list that is supposed to be showing them.
 */
export function orderScanCards(cards: ScanCard[], sort: TaskSort = 'newest'): ScanCard[] {
  const direction = sort === 'oldest' ? -1 : 1;
  return [...cards].sort((a, b) => direction * ((b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)));
}
